import cn from 'classnames'
import CloseButton from 'ext-ui/components/CloseButton/CloseButton'
import { white100 } from 'ext-ui/style/colors'
import { flyout_transitionSpeed } from 'ext-ui/style/transitionSpeeds'
import AiStarsIcon from 'ext-ui/svg/icon/AiStarsIcon'
import React, { ReactElement, useEffect, useState } from 'react'
import Modal, { Props as ModalProps } from 'react-modal'
import styled from 'styled-components'

import style from './BaseFlyout.style'

export type BaseFlyoutProps = Omit<ModalProps, 'onRequestClose'> & {
  className?: string
  /** If `true`, this Modal is open */
  isOpen: boolean
  /**
   * Use the `focus-background` variant when the content behind the flyout
   * shouldn't be fully obscured (e.g. for a filters flyout).
   *
   * Use the `default` variant in all other cases.
   */
  variant?: 'default' | 'focus-background'
  /** Function to run on close button click */
  onClose: () => void
  /**
   * Shows the primary content close button if `true` (default). Set `false` for
   * consumers whose content renders its own close affordance (e.g. the legacy
   * `RightFlyout`, whose content already provides a header + close button).
   */
  showCloseButton?: boolean
  /** `id` applied to the primary content box (`.BaseFlyout-Content`). */
  contentId?: string
  /** `id` applied to the primary content body (`.BaseFlyout-Body`). */
  bodyId?: string
  title?: string
  subtitle?: string
  /**
   * Decorative artwork for the header band, anchored to its top-right corner
   * and clipped by the panel edge (the "card creation" corner the legacy
   * action flyouts use). Purely presentational: it is rendered `aria-hidden`
   * and non-interactive, and it sits behind the close control.
   *
   * Only meaningful alongside `title`/`subtitle` — without a header there is no
   * band for it to decorate, and it is ignored. It renders inside the close bar
   * (the band's top row), so it also requires `showCloseButton`.
   */
  decoration?: React.ReactNode
  secondaryFlyoutContent?: {
    panelTab?: 'new'
    tabAriaLabel?: string
    ariaLabel?: string
    closeButtonAriaLabel?: string
    /** An optional panel-specific hook for sizing and spacing overrides. */
    className?: string
    title?: string
    subtitle?: string
    /** Hides the generic close button when the panel owns its close control. */
    hideCloseButton?: boolean
    /** Keeps this panel continuously visible without rendering a panel tab. */
    alwaysOpen?: boolean
    content?: React.ReactNode
    isPanelOpen?: boolean
    onPanelOpenChange?: (isOpen: boolean) => void
    /** Lets a secondary panel own a control which closes just that panel. */
    renderContent?: (closeSecondary: () => void) => React.ReactNode
  }
  defaultSecondaryFlyoutOpen?: boolean
}

function BaseFlyout(
  props: BaseFlyoutProps & { 'aria-label': string },
): ReactElement {
  const {
    className = '',
    isOpen,
    variant = 'default',
    children,
    onClose,
    showCloseButton = true,
    contentId,
    bodyId,
    title,
    subtitle,
    decoration,
    secondaryFlyoutContent,
    defaultSecondaryFlyoutOpen = false,
    'aria-label': ariaLabel,
    // ...rest
  } = props

  const [showSecondaryContent, setShowSecondaryContent] = useState(
    defaultSecondaryFlyoutOpen,
  )
  const secondaryFlyoutIsAlwaysOpen =
    secondaryFlyoutContent?.alwaysOpen ?? false
  const isSecondaryContentVisible =
    secondaryFlyoutContent?.isPanelOpen ??
    (secondaryFlyoutIsAlwaysOpen || showSecondaryContent)
  const setSecondaryContentVisible = (isVisible: boolean): void => {
    if (
      secondaryFlyoutContent?.isPanelOpen === undefined &&
      !secondaryFlyoutIsAlwaysOpen
    ) {
      setShowSecondaryContent(isVisible)
    }
    secondaryFlyoutContent?.onPanelOpenChange?.(isVisible)
  }
  const closeSecondary = (): void => setSecondaryContentVisible(false)

  /*
   * Single source of truth for "this flyout has a header band". The decoration
   * decorates that band, so it must appear on exactly the same condition as the
   * header itself — otherwise a consumer passing `decoration` without a title
   * gets corner artwork floating over a bare close bar.
   */
  const hasHeader = Boolean(title || subtitle)

  useEffect(() => {
    setShowSecondaryContent(defaultSecondaryFlyoutOpen)
  }, [defaultSecondaryFlyoutOpen])

  return (
    <Modal
      className={cn('BaseFlyout-Container', variant)}
      overlayClassName={{
        base: 'BaseFlyout-Overlay',
        afterOpen: 'BaseFlyout-Overlay-open',
        beforeClose: 'BaseFlyout-Overlay-closed',
      }}
      portalClassName={cn('BaseFlyout', className)}
      isOpen={isOpen}
      onRequestClose={onClose}
      closeTimeoutMS={parseInt(flyout_transitionSpeed)}
      bodyOpenClassName="modal-open"
      testId="flyout"
      contentLabel={ariaLabel}
    >
      {secondaryFlyoutContent && (
        <>
          {!secondaryFlyoutIsAlwaysOpen &&
            secondaryFlyoutContent.panelTab !== undefined && (
              <button
                className={cn(
                  'BaseFlyout-PanelTab',
                  secondaryFlyoutContent.panelTab,
                  {
                    showSecondaryContent: isSecondaryContentVisible,
                  },
                )}
                data-testid={'panelTab'}
                aria-label={secondaryFlyoutContent.tabAriaLabel}
                onClick={() => setSecondaryContentVisible(true)}
              >
                <AiStarsIcon fill={white100} />
              </button>
            )}
          <aside
            data-testid={'secondaryFlyout'}
            aria-label={secondaryFlyoutContent.ariaLabel}
            className={cn(
              'BaseFlyout-Content secondaryFlyout',
              secondaryFlyoutContent.className,
              secondaryFlyoutContent.panelTab,
              {
                showSecondaryContent: isSecondaryContentVisible,
              },
            )}
          >
            {!secondaryFlyoutContent.hideCloseButton &&
              !secondaryFlyoutIsAlwaysOpen && (
                <div className="BaseFlyout-CloseBar">
                  <CloseButton
                    data-testid={'closeSecondaryFlyout'}
                    className="BaseFlyout-CloseBar-CloseButton"
                    variant="dark-on-light"
                    aria-label={secondaryFlyoutContent.closeButtonAriaLabel}
                    onClick={closeSecondary}
                  />
                </div>
              )}
            {(secondaryFlyoutContent.title ||
              secondaryFlyoutContent.subtitle) && (
              <header className="BaseFlyout-Header">
                {secondaryFlyoutContent.title && (
                  <h2>{secondaryFlyoutContent.title}</h2>
                )}
                {secondaryFlyoutContent.subtitle && (
                  <p>{secondaryFlyoutContent.subtitle}</p>
                )}
              </header>
            )}
            <div className="BaseFlyout-Body">
              {secondaryFlyoutContent.renderContent
                ? secondaryFlyoutContent.renderContent(closeSecondary)
                : secondaryFlyoutContent.content}
            </div>
          </aside>
        </>
      )}
      <div
        className={'BaseFlyout-Content'}
        data-testid={'primaryFlyout'}
        id={contentId}
      >
        {showCloseButton && (
          <div className="BaseFlyout-CloseBar">
            {decoration !== undefined && hasHeader && (
              <div className="BaseFlyout-Decoration" aria-hidden="true">
                {decoration}
              </div>
            )}
            <CloseButton
              data-testid={'closePrimaryFlyout'}
              className="BaseFlyout-CloseBar-CloseButton"
              variant="dark-on-light"
              onClick={onClose}
            />
          </div>
        )}
        {hasHeader && (
          <header className="BaseFlyout-Header">
            {title && <h2>{title}</h2>}
            {subtitle && <p>{subtitle}</p>}
          </header>
        )}
        <div className="BaseFlyout-Body" id={bodyId}>
          {children}
        </div>
      </div>
    </Modal>
  )
}

// We have to use `styled` instead of the `css` prop because the `css` prop
// will automatically put the styled-components mangled className in the
// `className` prop on `<Flyout>`. However, that puts the class on the Content,
// preventing us from styling the Overlay. By using `styled`, we are able to
// pass the className to `portalClassName`, which will style the parent of both
// Overlay and Content.
export default styled(BaseFlyout)`
  ${style}
`
